import { FC } from "react";
import AdequacyDetailInfoTemplate from "../adequacy/AdequacyDetailInfoTemplate";
import { AdequacySummarySpecialtyGroup } from "../../results/adequacy";
import { isNullOrUndefined } from "../../../shared/utility";
import { CalculationMethodEnum } from "../../../templates/templateModels";
import { ProjectRunConfigurationResult } from "../../projectRunConfigurationResult";
import ProjectOptions from "../../projectOptions";

interface MapDetailsBoxProps {
  adequacySummarySpecialtyGroup: AdequacySummarySpecialtyGroup;
  cmsCreditIsApplying: boolean;
  project: ProjectOptions;
  runConfiguration: ProjectRunConfigurationResult;
}

const MapDetailsBox: FC<MapDetailsBoxProps> = ({ cmsCreditIsApplying, adequacySummarySpecialtyGroup, project, runConfiguration }) => {
  const totalMembers = !isNullOrUndefined(adequacySummarySpecialtyGroup?.accessSummary?.fullMemberCount)
    ? adequacySummarySpecialtyGroup.accessSummary.fullMemberCount.toLocaleString()
    : "---";

  const membersWithAccess = !isNullOrUndefined(adequacySummarySpecialtyGroup?.accessSummary?.fullMemberCountWithAccess)
    ? adequacySummarySpecialtyGroup.accessSummary.fullMemberCountWithAccess.toLocaleString()
    : "---";

  const membersWithAccessPercent = (adequacySummarySpecialtyGroup?.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && adequacySummarySpecialtyGroup.accessSummary.memberAccessPercentage.toFixed(1)) || "---";

  const membersWithoutAccess = !isNullOrUndefined(adequacySummarySpecialtyGroup?.accessSummary?.fullMemberCountWithoutAccess)
    ? adequacySummarySpecialtyGroup.accessSummary.fullMemberCountWithoutAccess.toLocaleString()
    : "---";

  const membersWithoutAccessPercent = (adequacySummarySpecialtyGroup?.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && (100 - adequacySummarySpecialtyGroup.accessSummary.memberAccessPercentage).toFixed(1)) || "---";

  const servicingProviderCount = !isNullOrUndefined(adequacySummarySpecialtyGroup?.servicingSummary?.distinctServicingProviderCount)
    ? adequacySummarySpecialtyGroup.servicingSummary.distinctServicingProviderCount.toLocaleString()
    : "---";

  const inCountyServicingProviderCount = !isNullOrUndefined(adequacySummarySpecialtyGroup?.servicingSummary?.inRegionProviderCount)
    ? adequacySummarySpecialtyGroup.servicingSummary.inRegionProviderCount.toLocaleString()
    : "---";

  const isTimeOnlyCalculationMethod = !!adequacySummarySpecialtyGroup?.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.calculationMethod.toString() === (CalculationMethodEnum.Time.toLowerCase());

  const isDistanceOnlyCalculationMethod = !!adequacySummarySpecialtyGroup?.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.calculationMethod.toString() === (CalculationMethodEnum.Distance.toLowerCase());

  const avgDistance = (!isTimeOnlyCalculationMethod
    && adequacySummarySpecialtyGroup.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && adequacySummarySpecialtyGroup.accessSummary.averageCoveringDistance
    && adequacySummarySpecialtyGroup.accessSummary.averageCoveringDistance.toFixed(1)) || "---";

  const maxDistance = (!isTimeOnlyCalculationMethod
    && adequacySummarySpecialtyGroup.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && adequacySummarySpecialtyGroup.accessSummary.maximumDistance
    && adequacySummarySpecialtyGroup.accessSummary.maximumDistance.toFixed(1)) || "---";

  const avgTime = (!isDistanceOnlyCalculationMethod
    && adequacySummarySpecialtyGroup.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && adequacySummarySpecialtyGroup.accessSummary.averageCoveringTime
    && adequacySummarySpecialtyGroup.accessSummary.averageCoveringTime.toFixed(1)) || "---";

  const maxTime = (!isDistanceOnlyCalculationMethod
    && adequacySummarySpecialtyGroup.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && adequacySummarySpecialtyGroup.accessSummary.maximumTime !== undefined
    && adequacySummarySpecialtyGroup.accessSummary.maximumTime.toFixed(1)) || "---";

  const showAvgDist = runConfiguration.showDistance && project.showAverageDistance;
  const showMaxDist = runConfiguration.showDistance && project.showMaximumDistance;
  const showAvgTime = runConfiguration.showTime && project.showAverageTime;
  const showMaxTime = runConfiguration.showTime && project.showMaximumTime;

  return (
    <div className="provider-member-mapping__map-details-container">
      <h2>Map Of:</h2>
      <AdequacyDetailInfoTemplate footnote={adequacySummarySpecialtyGroup?.footnote} cmsCreditIsApplying={cmsCreditIsApplying} />
      <div className="details">
        <div className="detail-item">
          <strong>Servicing Provider Count:</strong> {servicingProviderCount}
        </div>
        <div className="detail-item">
          <strong>In-County Servicing Provider Count:</strong> {inCountyServicingProviderCount}
        </div>
        <div className="detail-item">
          <strong>Total Members:</strong> {totalMembers}
        </div>
        <div className="detail-item">
          <strong>Members with Access:</strong> {membersWithAccess} ({membersWithAccessPercent}%)
        </div>
        <div className="detail-item">
          <strong>Members without Access:</strong> {membersWithoutAccess} ({membersWithoutAccessPercent}%)
        </div>
        {showAvgDist && (
          <div className="detail-item">
            <strong>Average Distance (miles):</strong> {avgDistance}
          </div>
        )}
        {showMaxDist && (
          <div className="detail-item">
            <strong>Max Distance (miles):</strong> {maxDistance}
          </div>
        )}
        {showAvgTime && (
          <div className="detail-item">
            <strong>Average Time (minutes):</strong> {avgTime}
          </div>
        )}
        {showMaxTime && (
          <div className="detail-item">
            <strong>Max Time (minutes):</strong> {maxTime}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapDetailsBox;